/**
 * MetaDoc (knowledge-document) API service.
 *
 * Promise-based wrapper around the real backend endpoint under
 * `api/v0/MetaCommunity`. Mirrors the axios/base-URL/cookie-auth pattern used
 * by the rest of ClientApp (`src/api/call.js`, `services/*`). All responses are
 * wrapped in the standard ResponseModel `{ message, status, data }`; the helpers
 * below unwrap `.data` and throw on a non-success `status`
 * (APIStatus.Successful === 0) so callers can `await` the inner payload directly
 * and `catch` a friendly Error.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc.
 *
 * @typedef {Object} AvailableMetaDocumentDto
 * @property {string} vectorDbName @property {number} chunkCount @property {string} title
 * @property {string} subTitle @property {string} createBy @property {?string} createDate
 * @property {?string} updateDate
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) {
      return body.data;
    }
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    err.data = body.data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

export const metaDocApi = {
  /**
   * Available MetaDoc documents for the knowledge picker (dropdown source).
   * @returns {Promise<Array>} list of { vectorDbName, title, subTitle, chunkCount, ... }
   */
  getAvailableDocuments({ category = null, type = null } = {}) {
    const qs = [];
    if (category) qs.push(`category=${encodeURIComponent(category)}`);
    if (type) qs.push(`type=${encodeURIComponent(type)}`);
    const suffix = qs.length ? `?${qs.join('&')}` : '';
    return get(`api/v0/MetaCommunity/GetAvailableMetaDocuments${suffix}`);
  },
};

export default metaDocApi;
